import { fetchAllPages } from "../utils/fetchAllPages";
import { s3ProgressConfig } from "../utils/uploadProgress";
import { api } from "./axiosClient";
import { getCurrentUser } from "../auth/auth.service";
import type { Obligation } from "../types/obligation.types";
import {
  ComplianceStatus,
  ObligationStatus,
} from "../features/obligations/obligationWorkflow";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { buildUploadPayload } from "../utils/mediaUtils";
import type {
  AssignmentRoutingPreview,
  ObligationComment,
  ObligationEmailThread,
  ObligationSummary,
  ObligationAuditLogRow,
} from "../features/obligations/obligationWorkflow";
import type { ObligationDependencyGraph } from "../features/obligations/obligationDependency";

/** A fresh request id per state-changing call, so the backend can tell a retry from a new action. */
const withRequestId = () => ({ headers: { "x-requestid": uuidv4() } });

export const obligationsApi = {
  /**
   * Every obligation in the current organisation, for the Home and
   * Dashboard summaries. This used to return mock data; when the mock was
   * removed the dashboards still called it, so both threw on load
   * ("getObligations is not a function") and Home stayed at zero.
   * Kept as a thin wrapper over the real search so those callers work.
   */
  getObligations: async (): Promise<{ data: Obligation[] }> => {
    const currentUser = await getCurrentUser();
    // Every page, not just the first 200: the dashboards count all of them.
    const rows: any[] = await fetchAllPages(async (pageIndex, pageSize) => {
      const response = await api.post<any>("/obligation/search", {
        companyId: currentUser?.companyId,
        isAppAdmin: currentUser?.isAppAdmin ?? false,
        pageSize,
        pageIndex,
        orderBy: "",
        sortAscending: false,
        includeFacets: false,
        groupIds: [],
        includeDocument: false,
        ids: [],
        isActive: null,
        createdBy: [],
        updatedBy: [],
        currentProfileId: currentUser?.id ?? currentUser?.userId,
        myMode: true,
        searchText: "",
        includeDeleted: false,
        // Work items only: a recurring obligation is counted through its periods, which are what falls due.
        excludeSeries: true,
      });
      // The body is `{ count, data: [...] }`, possibly wrapped once more. Reading `data` first
      // took the row array itself, whose missing count made every result look like one page
      // and dropped everything past the first 200.
      const body = Array.isArray(response.data?.data)
        ? response.data
        : (response.data?.data ?? response.data);
      const pageRows: any[] = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      return { rows: pageRows, total: Number(body?.count ?? pageRows.length) };
    });
    // Into the summary shape the dashboards read: "non-compliant" is the
    // recorded outcome and nothing else, Closed is "completed", every other
    // stage is "upcoming". An obligation's end date is its due date.
    //
    // Overdue used to land in "non-compliant" too, which made every dashboard
    // card and chart that counts it report overdue work as a failed outcome:
    // a business unit with two non-compliant obligations read thirty. Overdue
    // has its own count, taken from the due date, so nothing is lost here.
    const summaryStatus = (r: any): Obligation["status"] => {
      if (Number(r.complianceStatus) === ComplianceStatus.NonCompliant)
        return "non-compliant";
      return Number(r.obligationStatus) === ObligationStatus.Closed
        ? "completed"
        : "upcoming";
    };
    const data: Obligation[] = rows
      .filter((r) => r && !r.deletedBy)
      .map((r) => ({
        id: r.id,
        createdAt: r.creationDate ?? undefined,
        contractId: r.contractId ?? "",
        contractTitle: r.contractTitle ?? r.title ?? "",
        title: r.title ?? "",
        function: r.departmentName ?? "",
        category: "Compliance",
        status: summaryStatus(r),
        dueDate: r.endDate ?? r.startDate ?? "",
        businessUnitId: r.businessUnitId ?? "",
        businessUnitName: r.businessUnitName ?? "",
        assignedStaffName: r.assigneeName ?? "",
        assigneeId: r.assigneeId ?? undefined,
        complianceStatus: Number(r.complianceStatus ?? 0),
        occurrenceType: Number(r.occurenceType ?? 0) || undefined,
        workflowStatus: Number(r.obligationStatus ?? 0) || undefined,
        partyIds:
          r.secondParty?.partyId &&
          r.secondParty.partyId !== "00000000-0000-0000-0000-000000000000"
            ? [String(r.secondParty.partyId)]
            : [],
        partyNames: [
          r.secondParty?.registeredIndividualName,
          r.secondParty?.brandName,
        ].filter(Boolean),
      }));
    return { data };
  },

  /**
   * The rows of a search result. The API answers `{ count, data: [...] }`, and
   * callers hold it at different depths (the axios body, or an already unwrapped
   * envelope), so this reads whichever it is given instead of guessing.
   */
  rowsOf: (result: any): any[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.data?.data)) return result.data.data;
    return [];
  },

  searchObligations: async (payload: any) => {
    const response = await api.post<any>("/obligation/search", payload);

    return response.data;
  },
  searchObligationsContracts: async (payload: any) => {
    const response = await api.post<any>("/obligation/searchContract", payload);

    return response.data;
  },
  createObligation: async (data: any) => {
    const response = await api.post<any>("/obligation/create", data);

    return response.data;
  },

  updateObligation: async (data: any) => {
    const response = await api.post<any>("/obligation/edit", data);

    return response.data;
  },

  activateObligation: async (id: string) => {
    const response = await api.post<any>("/obligation/activate", { id });

    return response.data;
  },

  deActivateObligation: async (id: string) => {
    const response = await api.post<any>("/obligation/deactivate", { id });

    return response.data;
  },

  /** Soft delete; the reason is required and kept on the obligation's audit trail. */
  removeObligation: async (id: string, reason: string) => {
    const response = await api.post<any>("/obligation/remove", { id, reason });

    return response.data;
  },

  /** Undoes a removal; it comes back active or inactive, as it was when removed. */
  restoreObligation: async (id: string) => {
    const response = await api.post<any>("/obligation/remove", {
      id,
      undo: true,
    });

    return response.data;
  },

  createObligationContract: async (data: any) => {
    // Nullable Guids on the server reject "": send null for an absent contract.
    const blankToNull = (value: unknown) =>
      value === "" || value === undefined ? null : value;
    const payload = {
      ...data,
      existingContractId: blankToNull(data?.existingContractId),
      obligationContract: data?.obligationContract
        ? {
            ...data.obligationContract,
            connectedContractNumber: blankToNull(
              data.obligationContract.connectedContractNumber,
            ),
          }
        : data?.obligationContract,
    };
    const response = await api.post<any>(
      "/obligation/createObgContract",
      payload,
    );

    return response.data;
  },

  searchObligationsContract: async (payload: any) => {
    const response = await api.post<any>("/obligation/searchContract", payload);

    return response.data;
  },

  initUpload: async (ids: string[], groupId?: string) => {
    const response = await api.post("/obligation/initupload", {
      ids,
      groupId,
    });
    return response.data;
  },

  uploadToS3: async (s3Url: string, file: File) => {
    return await axios.put(s3Url, file, {
      ...s3ProgressConfig(file),
      headers: { "Content-Type": file.type },
    });
  },

  uploadMedia: async (_obligationId: string, medias: any[]) => {
    const response = await api.post("/obligation/uploadmedia", {
      medias,
    });
    return response.data;
  },

  getMediaLink: async (
    ids: string[],
    size: number = 3,
    groupId?: string,
  ): Promise<{ links: Array<{ id: string; link: string }> }> => {
    const response = await api.post("/obligation/getmedialink", {
      ids,
      size,
      groupId,
    });
    return response.data;
  },

  /** Where an obligation for this department will land, per business unit. */
  previewAssignment: async (
    departmentId: string,
    businessUnitIds: string[] = [],
  ): Promise<AssignmentRoutingPreview> => {
    const response = await api.post("/obligation/assignmentpreview", {
      departmentId,
      businessUnitIds,
    });
    const body = response.data?.data ?? response.data;
    return {
      availableBusinessUnits: body?.availableBusinessUnits ?? [],
      routes: body?.routes ?? [],
    };
  },

  assignObligation: async (
    obligationId: string,
    assigneeId: string,
    assigneeName: string,
  ) => {
    const response = await api.post(
      "/obligation/assign",
      { obligationId, assigneeId, assigneeName },
      withRequestId(),
    );
    return response.data;
  },

  setRecurrence: async (obligationId: string, recurrenceCycle: number) => {
    const response = await api.post(
      "/obligation/setrecurrence",
      { obligationId, recurrenceCycle },
      withRequestId(),
    );
    return response.data;
  },

  /**
   * Resolves to the status the backend actually recorded, which is Delayed
   * Compliant when Compliant was requested on an overdue obligation.
   */
  markCompliance: async (
    obligationId: string,
    complianceStatus: number,
    remarks: string,
  ): Promise<{ requestedStatus: number; complianceStatus: number }> => {
    const response = await api.post(
      "/obligation/markcompliance",
      { obligationId, complianceStatus, remarks },
      withRequestId(),
    );
    return response.data?.data ?? response.data;
  },

  /**
   * Puts the files in storage and returns the media records the evidence
   * endpoints expect. Nothing is attached to the obligation until those
   * records are sent with submitEvidence or respondNeedMoreInfo.
   */
  stageEvidenceFiles: async (obligationId: string, files: File[]) => {
    if (!files.length) return [];
    const staged = buildUploadPayload(obligationId, files);
    const init = await obligationsApi.initUpload(staged.map((item) => item.id));
    await Promise.all(
      staged.map(async (item) => {
        const target = init?.links?.find((link: any) => link.id === item.id)?.t;
        if (!target) throw new Error(`No upload slot for ${item.file.name}`);
        await obligationsApi.uploadToS3(target, item.file);
      }),
    );
    return staged.map((item) => item.payload);
  },

  submitEvidence: async (obligationId: string, note: string, medias: any[]) => {
    const response = await api.post(
      "/obligation/submitevidence",
      { obligationId, note, medias },
      withRequestId(),
    );
    return response.data;
  },

  /** A soft delete: the files are flagged as removed, never erased, and the reason goes into the audit trail. */
  removeEvidence: async (
    obligationId: string,
    mediaIds: string[],
    reason: string,
  ) => {
    const response = await api.post(
      "/obligation/removeevidence",
      { obligationId, mediaIds, reason },
      withRequestId(),
    );
    return response.data;
  },

  raiseNeedMoreInfo: async (obligationId: string, comment: string) => {
    const response = await api.post(
      "/obligation/nmi/raise",
      { obligationId, comment },
      withRequestId(),
    );
    return response.data;
  },

  respondNeedMoreInfo: async (
    obligationId: string,
    comment: string,
    medias: any[],
  ) => {
    const response = await api.post(
      "/obligation/nmi/respond",
      { obligationId, comment, medias },
      withRequestId(),
    );
    return response.data;
  },

  /** Counts for the compliance dashboard; the server limits them to what the caller is responsible for. */
  getSummary: async (filter: {
    businessUnitIds?: string[];
    departmentIds?: string[];
    dueFrom?: string;
    dueTo?: string;
  }): Promise<ObligationSummary> => {
    const response = await api.post("/obligation/summary", filter);
    return response.data?.data ?? response.data;
  },

  /** The obligation's email thread, and the address evidence can be sent to. */
  getEmailThread: async (
    obligationId: string,
  ): Promise<ObligationEmailThread> => {
    const response = await api.post("/obligation/getemailthread", {
      obligationId,
    });
    const body = response.data?.data ?? response.data;
    return {
      emailAddress: body?.emailAddress ?? "",
      messages: body?.messages ?? [],
    };
  },

  getComments: async (obligationId: string): Promise<ObligationComment[]> => {
    const response = await api.post("/obligation/getcomments", {
      obligationId,
    });
    const body = response.data?.data ?? response.data;
    return Array.isArray(body) ? body : [];
  },

  addComment: async (obligationId: string, text: string) => {
    const response = await api.post(
      "/obligation/addcomment",
      { obligationId, text },
      withRequestId(),
    );
    return response.data;
  },

  /** status: 1 Unresolved, 2 Resolved, 3 Won't Fix (needs a reason). */
  updateCommentStatus: async (
    obligationId: string,
    commentId: string,
    status: number,
    reason?: string,
  ) => {
    const response = await api.post(
      "/obligation/updatecommentstatus",
      { obligationId, commentId, status, reason },
      withRequestId(),
    );
    return response.data;
  },

  // ------------------------------------------------------------ connected obligations

  /** The chain an obligation belongs to: its prerequisites, what waits for it, and their states. */
  getDependencies: async (
    obligationId: string,
  ): Promise<ObligationDependencyGraph> => {
    const response = await api.get(`/obligation/dependencies/${obligationId}`);
    const body = response.data?.data ?? response.data;
    return {
      obligationId: body?.obligationId ?? obligationId,
      nodes: body?.nodes ?? [],
      edges: body?.edges ?? [],
      truncated: Boolean(body?.truncated),
      canManage: Boolean(body?.canManage),
      canConfirm: Boolean(body?.canConfirm),
    };
  },

  /** Sets the complete list of prerequisites; an empty list makes it independent. */
  setDependencies: async (
    obligationId: string,
    predecessorIds: string[],
    dueDateMode?: number | null,
    reason?: string,
  ) => {
    const response = await api.post(
      "/obligation/setdependencies",
      {
        obligationId,
        predecessorIds,
        dueDateMode: dueDateMode ?? null,
        reason,
      },
      withRequestId(),
    );
    return response.data;
  },

  replaceDependency: async (
    obligationId: string,
    oldPredecessorId: string,
    newPredecessorId: string,
    reason: string,
  ) => {
    const response = await api.post(
      "/obligation/replacedependency",
      { obligationId, oldPredecessorId, newPredecessorId, reason },
      withRequestId(),
    );
    return response.data;
  },

  removeDependency: async (
    obligationId: string,
    predecessorId: string,
    reason: string,
  ) => {
    const response = await api.post(
      "/obligation/removedependency",
      { obligationId, predecessorId, reason },
      withRequestId(),
    );
    return response.data;
  },

  confirmStart: async (
    obligationId: string,
    effectiveStartDate: string,
    dueDateMode: number | null,
    reason?: string,
  ) => {
    const response = await api.post(
      "/obligation/confirmstart",
      { obligationId, effectiveStartDate, dueDateMode, reason },
      withRequestId(),
    );
    return response.data;
  },

  resolveDependency: async (
    obligationId: string,
    resolution: number,
    note: string,
  ) => {
    const response = await api.post(
      "/obligation/resolvedependency",
      { obligationId, resolution, note },
      withRequestId(),
    );
    return response.data;
  },

  /** Org/Super Admin: sends every due obligation reminder now instead of at the next sweep. */
  sendReminders: async (): Promise<{ remindedCount: number }> => {
    const response = await api.post("/obligation/sendreminders");
    const body = response.data?.data ?? response.data;
    return { remindedCount: Number(body?.remindedCount ?? 0) };
  },

  getAuditLog: async (
    obligationId: string,
  ): Promise<ObligationAuditLogRow[]> => {
    const response = await api.get(`/obligation/auditlog/${obligationId}`);
    const body = response.data?.data ?? response.data;
    return body?.rows ?? [];
  },
};
